import { useGoogleLogin } from '@react-oauth/google';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { useRef } from 'react';

import { useAppDispatch } from 'hooks/useRedux';
import { addUser, IUser } from 'store/slices/auth';
import { api } from 'lib/axios';
import { GoogleError, GoogleRequest, GoogleResponse } from './types';
import { HOME_ROUTE } from 'constants/routes';

type UseHandleWithGoogleProps = {
  usagePurpose?: string;
  phone?: string;
};

export const useHandleWithGoogle = ({
  usagePurpose,
  phone,
}: UseHandleWithGoogleProps) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const keyRef = useRef<string>('');

  const {
    isError: isErrorGoogle,
    error: errorGoogle,
    isLoading: isLoadingGoogle,
    mutateAsync,
  } = useMutation<
    GoogleResponse,
    AxiosError<GoogleError>,
    GoogleRequest,
    GoogleResponse
  >((args) => api.post('/auth/google/', args).then((res) => res.data));

  const {
    isError: isErrorMe,
    error: errorMe,
    isFetching: isFetchingMe,
  } = useQuery<IUser, AxiosError<GoogleError>>(
    ['me', keyRef.current],
    async ({ queryKey }) => {
      const { data } = await api.get<IUser>('/me', {
        headers: {
          Authorization: `Token ${queryKey[1]}`,
        },
      });
      return data;
    },
    {
      enabled: !!keyRef.current,
      onSuccess: (data) => {
        dispatch(
          addUser({
            jwt: { access: `Token ${keyRef.current}`, refresh: keyRef.current },
            data,
          })
        );
        router.replace(HOME_ROUTE);
      },
    }
  );

  const handleGoogle = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      const { key } = await mutateAsync({
        accessToken: codeResponse.access_token,
        usagePurpose,
        phone,
      });
      keyRef.current = key;
    },
    onError: (resErr) => {
      console.error(resErr);
    },
  });

  return {
    handleGoogle,
    loading: isLoadingGoogle || isFetchingMe,
    isError: isErrorMe || isErrorGoogle,
    error: errorMe || errorGoogle,
  };
};
